import { Fragment, useEffect } from "react";
import { Box, useThemeUI } from "theme-ui";
import { useDispatch, useSelector } from "react-redux";
import omit from "lodash/fp/omit";
import castArray from "lodash/castArray";
import get from "lodash/fp/get";
import { selectSelectedProduct, selectUIVersion } from "store/app";

import {
  updateUserChoiceValues,
  displayInfoModal,
  setTouchedField,
} from "store/app/appSlice";
import { Button } from "components";
import { ChevronLeftIcon } from "components/icons";
import FieldsSelector from "./FieldsSelector";
import { conditions } from "utils/shouldBeDisplayed";

export function getIsDisabled({ isDisabledIf, userChoiceValues }) {
  return Object.keys(isDisabledIf).some((key) => {
    const { includes, notIncludes } = isDisabledIf[key];
    if (includes && castArray(includes).includes(userChoiceValues[key] || "")) {
      return true;
    }
    if (
      notIncludes &&
      castArray(!notIncludes).includes(userChoiceValues[key] || "")
    ) {
      return true;
    }
    return false;
  });
}

export default function FieldsSelectorWrapper({
  customisationTitle,
  component,
  userChoices,
  userChoiceValues,
  name,
  isDisabled: isDisabledOverride,
  isDisabledIf = {},
  isRequired,
  width,
  defaultValueToSetOnRender,
  toolTip,

  // for <Checkbox />
  label,
  labelIcon,
  checkedValue,
  uncheckedValue,

  // for <Input />
  as,
  type,
  initialValue,
  maxLength,
  placeholder,
  hint,
  info,

  // For Buttons
  options = [],
  fields = [],

  hasOptionalFields,

  focusedField,
  setFocusedField,
  goBackFocusedFields,
  allowUnselect,
  onChange = () => {},

  resetButtonOption,
  goBackOnSelect,

  props = {},
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const UIVersion = useSelector(selectUIVersion);

  const dispatch = useDispatch();
  const selectedProduct = useSelector(selectSelectedProduct).id;

  const fieldsTouched = useSelector(get(["app", "fieldsTouched"]));

  const dispatchSetTouchedField = (nameOveride) => {
    dispatch(setTouchedField(nameOveride || name));
  };

  const dispatchUpdateUserChoices = (value, nameOveride) => {
    const valuesToUpdate = [
      {
        name: nameOveride || name,
        value,
        // value: userChoiceValues[name] !== value ? value : null,
      },
    ];

    // clear "Holder Length" if one of these values changes (Skate)
    if (["size_left", "size_right", "Fit", "Blade Type"].includes(name)) {
      valuesToUpdate.push({
        name: "Holder Length",
        value: null,
      });
    }

    // clear "Eyelets" if "size_left", "size_right" is 4 or less, or there is 3/4 difference between the two (Skate)
    if (
      ["size_left", "size_right"].includes(name) &&
      userChoiceValues.Eyelets === "HyperFlex Eyelets" &&
      conditions.skateBootLengthCheck(userChoiceValues) &&
      conditions.skateBootLengthCheckForHolder(userChoiceValues)
    ) {
      valuesToUpdate.push({
        name: "Eyelets",
        value: null,
      });
    }

    if (
      ["size_left", "size_right"].includes(name) &&
      userChoiceValues.Holder === "Powerfly" &&
      conditions.skateBootLengthCheckForHolder(userChoiceValues)
    ) {
      valuesToUpdate.push({
        name: "Holder",
        value: "LS Edge",
      });
    }

    // update userChoiceValues["Shoulder Decal Color code"] value if Goalie Shaft is changed from Black
    if (
      name === "Shaft Color code" &&
      userChoiceValues["Shaft Color code"] === "BLACK/BLACK"
    ) {
      valuesToUpdate.push({
        name: "Shoulder Decal Color code",
        value: "WHITE/RED",
      });
    }
    // update userChoiceValues["Shaft Color code"] value if Goalie Shoulder is changed from Black
    if (
      name === "Shoulder Decal Color code" &&
      userChoiceValues["Shoulder Decal Color code"] === "BLACK/BLACK"
    ) {
      valuesToUpdate.push({
        name: "Shaft Color code",
        value: "WHITE/RED",
      });
    }

    // ! DEV Changing t-trap from "double" to "single" removes t-spine-lace--glove as an option, changing it back "double" adds it back but it doesn't apply it's default value. Temporary fix here, be good to fix properly
    if (
      ["t-trap"].includes(name) &&
      ["(Shadow) Double", "Double", "(S60) Double"].includes(value)
    ) {
      valuesToUpdate.push({
        name: "t-spine-lace--glove",
        value: "White",
      });
    }

    if (["glove-construction"].includes(name)) {
      valuesToUpdate.push({
        name: "t-trap-reinforced",
        value: value === "Vapor 90",
      });
    }

    // Graphic is a hard reset for values, but shouldn't reset handedness
    if (["graphic"].includes(name)) {
      valuesToUpdate.push({
        name: "full-right-hand",
        value: userChoiceValues["full-right-hand"],
      });
    }

    if (
      name === "outside-zone-color" &&
      value === "Black" &&
      userChoiceValues["bauer-logo-color"] === "Black"
    ) {
      valuesToUpdate.push({
        name: "bauer-logo-color",
        value: "White",
      });
    }

    const shadowLogoFillColorGloveIsWhite =
      userChoiceValues["shadow-logo-fill-color--glove"] === "White" ||
      userChoiceValues["shadow-logo-fill-color"] === "White";
    if (
      name === "shadow-logo-outline-color--glove" &&
      value === "White" &&
      shadowLogoFillColorGloveIsWhite
    ) {
      valuesToUpdate.push({
        name: "shadow-logo-fill-color--glove",
        value: "Black",
      });
    }

    const shadowLogoFillColorBlockerIsWhite =
      userChoiceValues["shadow-logo-fill-color--glove"] === "White" ||
      userChoiceValues["shadow-logo-fill-color"] === "White";
    if (
      name === "shadow-logo-outline-color--blocker" &&
      value === "White" &&
      shadowLogoFillColorBlockerIsWhite
    ) {
      valuesToUpdate.push({
        name: "shadow-logo-fill-color--blocker",
        value: "Black",
      });
    }

    dispatch(updateUserChoiceValues(valuesToUpdate));
    onChange(valuesToUpdate);
  };

  //Goalie Hyperlite2 Shaft and Shoulder relation
  useEffect(() => {
    if (userChoiceValues["Shaft Color code"] === "BLACK/BLACK") {
      dispatch(
        updateUserChoiceValues({
          name: "Shoulder Decal Color code",
          value: "BLACK/BLACK",
        })
      );
    }
  }, [userChoiceValues["Shaft Color code"]]);

  useEffect(() => {
    if (userChoiceValues["Shoulder Decal Color code"] === "BLACK/BLACK") {
      dispatch(
        updateUserChoiceValues({
          name: "Shaft Color code",
          value: "BLACK/BLACK",
        })
      );
    }
  }, [userChoiceValues["Shoulder Decal Color code"]]);

  //Goal Gear glove and blocker color zone logic
  //Goal gear logic
  useEffect(() => {
    const bauerLogoColor = userChoiceValues["bauer-logo-color--glove"];
    const hyp2rliteLogoColor = userChoiceValues["hyp2rlite-logo-color--glove"];
    if (
      (userChoiceValues["graphic"] === "vapor hyperlite" ||
        selectedProduct === "VaporFlyliteGoalieGear2025") &&
      userChoiceValues["outside-zone-color--glove"] === "White" &&
      (!bauerLogoColor || bauerLogoColor === "White")
    ) {
      dispatch(
        updateUserChoiceValues({
          name: "bauer-logo-color--glove",
          value: "Black",
        })
      );
    }
    if (
      (userChoiceValues["graphic"] === "vapor hyperlite" ||
        selectedProduct === "VaporFlyliteGoalieGear2025") &&
      userChoiceValues["outside-zone-color--glove"] === "White" &&
      (!hyp2rliteLogoColor || hyp2rliteLogoColor === "White")
    ) {
      dispatch(
        updateUserChoiceValues({
          name: "hyp2rlite-logo-color--glove",
          value: "Black",
        })
      );
    }
  }, [userChoiceValues["outside-zone-color--glove"] === "White"]);

  //Goal Gear Blocker
  useEffect(() => {
    const bauerLogoColor = userChoiceValues["bauer-logo-color--blocker"];
    const hyp2rliteLogoColor =
      userChoiceValues["hyp2rlite-logo-color--blocker"];
    if (
      (userChoiceValues["graphic"] === "vapor hyperlite" ||
        selectedProduct === "VaporFlyliteGoalieGear2025") &&
      userChoiceValues["outside-zone-color--blocker"] === "White" &&
      (!bauerLogoColor || bauerLogoColor === "White")
    ) {
      dispatch(
        updateUserChoiceValues({
          name: "bauer-logo-color--blocker",
          value: "Black",
        })
      );
    }
    if (
      (userChoiceValues["graphic"] === "vapor hyperlite" ||
        selectedProduct === "VaporFlyliteGoalieGear2025") &&
      userChoiceValues["outside-zone-color--blocker"] === "White" &&
      (!hyp2rliteLogoColor || hyp2rliteLogoColor === "White")
    ) {
      dispatch(
        updateUserChoiceValues({
          name: "hyp2rlite-logo-color--blocker",
          value: "Black",
        })
      );
    }
  }, [userChoiceValues["outside-zone-color--blocker"] === "White"]);

  //Shadow products
  //Shadow Blocker Logic
  useEffect(() => {
    const bauerLogoColor = userChoiceValues["bauer-logo-color--blocker"];
    if (
      (userChoiceValues["graphic"] === "supreme shadow" ||
        selectedProduct === "SupremeShadowGoalieGear2024") &&
      userChoiceValues["outside-bottom-color--blocker"] === "Black" &&
      (!bauerLogoColor || bauerLogoColor === "Black")
    ) {
      dispatch(
        updateUserChoiceValues({
          name: "bauer-logo-color--blocker",
          value: "White",
        })
      );
    }
  }, [userChoiceValues["middle-zone-color--blocker"] === "Black"]);

  //Shadow Glove Logic
  useEffect(() => {
    const bauerLogoColor = userChoiceValues["bauer-logo-color--glove"];
    if (
      (userChoiceValues["graphic"] === "supreme shadow" ||
        selectedProduct === "SupremeShadowGoalieGear2024") &&
      userChoiceValues["outside-bottom-color--glove"] === "Black" &&
      (!bauerLogoColor || bauerLogoColor === "Black")
    ) {
      dispatch(
        updateUserChoiceValues({
          name: "bauer-logo-color--glove",
          value: "White",
        })
      );
    }
  }, [userChoiceValues["middle-zone-color--glove"] === "Black"]);

  const selectedOption = options.find(
    (option) => option.value === userChoiceValues[name]
  );

  const subFields = selectedOption?.fields || [];
  const hasSubFields = subFields.length > 0;
  const renderSelectedOptionForSubFields =
    selectedOption &&
    selectedOption.hasOwnProperty("renderSelectedOptionForSubFields")
      ? selectedOption.renderSelectedOptionForSubFields
      : true;

  const optionsToRender =
    hasSubFields && !renderSelectedOptionForSubFields
      ? []
      : (hasSubFields ? [selectedOption].map(omit("hint")) : options).map(
          ({ isDisabledIf, ...option }) => {
            return {
              ...option,
              isDisabled:
                isDisabledIf &&
                getIsDisabled({ isDisabledIf, userChoiceValues }),
            };
          }
        );
  // conditional render selected option for hasSubFields, renderSelectedOptionForSubFields

  const isDisabled =
    isDisabledOverride !== undefined
      ? isDisabledOverride
      : getIsDisabled({ isDisabledIf, userChoiceValues });

  const goBack = () => {
    dispatchUpdateUserChoices(null);
    subFields.forEach((subField) => {
      dispatch(
        updateUserChoiceValues({
          name: subField.name,
          value: null,
        })
      );
    });
  };

  const displayInfo = info
    ? () => {
        dispatch(displayInfoModal(info));
      }
    : null;

  return (
    <>
      {hasSubFields && (
        <Button
          variant="secondary"
          size={isMobile ? "medium" : "large"}
          iconBefore={<ChevronLeftIcon />}
          aria-label="go back"
          isRound
          onClick={goBack}
        >
          {isMobile && "GO BACK"}
        </Button>
      )}

      <FieldsSelector
        customisationTitle={customisationTitle}
        component={component}
        userChoices={userChoices}
        userChoiceValues={userChoiceValues}
        name={name}
        isDisabled={isDisabled}
        isRequired={isRequired}
        isFieldTouched={fieldsTouched.includes(name)}
        setTouchedField={dispatchSetTouchedField}
        width={width}
        // for <Checkbox />
        label={label}
        labelIcon={labelIcon}
        checkedValue={checkedValue}
        uncheckedValue={uncheckedValue}
        // for <Input />
        as={as}
        type={type}
        initialValue={initialValue}
        maxLength={maxLength}
        placeholder={placeholder}
        hint={hint}
        displayInfo={displayInfo}
        // For Buttons
        options={optionsToRender}
        fields={fields}
        onChange={dispatchUpdateUserChoices}
        focusedField={focusedField}
        setFocusedField={setFocusedField}
        goBackFocusedFields={goBackFocusedFields}
        allowUnselect={allowUnselect}
        hasOptionalFields={hasOptionalFields}
        defaultValueToSetOnRender={defaultValueToSetOnRender}
        toolTip={toolTip}
        props={props}
        resetButtonOption={resetButtonOption}
        goBackOnSelect={goBackOnSelect}
      />

      {subFields.map((field) => (
        <Fragment key={field.name}>
          {field?.dividerLeft && (
            <Box
              sx={{
                width: UIVersion === 2 ? "2px" : isMobile ? "100%" : "2px",
                height: UIVersion === 2 ? "60px" : isMobile ? "2px" : "60px",
                background: "#D9D9D9",
              }}
            />
          )}

          <FieldsSelectorWrapper
            customisationTitle={customisationTitle}
            component={field.component}
            userChoices={userChoices}
            userChoiceValues={userChoiceValues}
            name={field.name}
            placeholder={field.placeholder}
            hint={field.hint}
            as={field.as}
            type={field.type}
            initialValue={field.initialValue}
            maxLength={field.maxLength}
            isRequired={field.isRequired}
            options={field.options}
            fields={fields}
            label={field.label}
            labelIcon={field.labelIcon}
            checkedValue={field.checkedValue}
            uncheckedValue={field.uncheckedValue}
            isDisabledIf={field.isDisabledIf}
            width={field.width}
            defaultValueToSetOnRender={field.defaultValueToSetOnRender}
            toolTip={field.toolTip}
            props={field.props}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            goBackFocusedFields={goBackFocusedFields}
          />
        </Fragment>
      ))}
    </>
  );
}
